import { useCallback, useEffect, useRef, useState } from "react";

import type {
  ParticipationAggregate,
  ParticipationApiId,
} from "../../shared/participation";

interface ParticipationSuccess {
  readonly ok: true;
  readonly data: ParticipationAggregate;
}

export interface ParticipationViewState {
  readonly data: ParticipationAggregate | null;
  readonly loading: boolean;
  readonly error: string | null;
}

export interface ParticipationController extends ParticipationViewState {
  readonly refreshNow: () => void;
}

export const PARTICIPATION_POLL_MS = 5_000;
export const PARTICIPATION_REQUEST_TIMEOUT_MS = 7_000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseParticipationPayload(
  value: unknown,
  expectedApiId: ParticipationApiId,
): ParticipationSuccess {
  if (!isRecord(value) || value.ok !== true || !isRecord(value.data)) {
    throw new Error("invalid participation response");
  }

  const data = value.data;
  const totalCount = data.totalCount;
  const authenticatedCount = data.authenticatedCount;
  const unauthenticatedCount = data.unauthenticatedCount;
  const participationRate = data.participationRate;
  const refreshedAt = data.refreshedAt;
  const fetchedAt = data.fetchedAt;
  const sourceGeneratedAt = data.sourceGeneratedAt;
  const dataVersion = data.dataVersion;

  if (
    data.apiId !== expectedApiId ||
    !Number.isSafeInteger(totalCount) ||
    (totalCount as number) < 0 ||
    !Number.isSafeInteger(authenticatedCount) ||
    (authenticatedCount as number) < 0 ||
    (authenticatedCount as number) > (totalCount as number) ||
    !Number.isSafeInteger(unauthenticatedCount) ||
    unauthenticatedCount !== (totalCount as number) - (authenticatedCount as number) ||
    typeof participationRate !== "number" ||
    !Number.isFinite(participationRate) ||
    participationRate < 0 ||
    participationRate > 100 ||
    participationRate !== ((totalCount as number) === 0
      ? 0
      : Math.round(
          ((authenticatedCount as number) / (totalCount as number)) * 1_000,
        ) / 10) ||
    !Number.isSafeInteger(dataVersion) ||
    (dataVersion as number) < 1 ||
    typeof refreshedAt !== "string" ||
    !Number.isFinite(Date.parse(refreshedAt)) ||
    typeof fetchedAt !== "string" ||
    !Number.isFinite(Date.parse(fetchedAt)) ||
    !(sourceGeneratedAt === null ||
      (typeof sourceGeneratedAt === "string" &&
        Number.isFinite(Date.parse(sourceGeneratedAt)))) ||
    typeof data.stale !== "boolean"
  ) {
    throw new Error("invalid participation data");
  }

  return {
    ok: true,
    data: {
      apiId: expectedApiId,
      totalCount: totalCount as number,
      authenticatedCount: authenticatedCount as number,
      unauthenticatedCount: unauthenticatedCount as number,
      participationRate,
      dataVersion: dataVersion as number,
      refreshedAt,
      sourceGeneratedAt: sourceGeneratedAt as string | null,
      fetchedAt,
      stale: data.stale,
    },
  };
}

export function useParticipation(
  apiId: ParticipationApiId,
  enabled: boolean,
): ParticipationController {
  const dataCacheRef = useRef<Partial<Record<ParticipationApiId, ParticipationAggregate>>>({});
  const refreshNowRef = useRef<() => void>(() => undefined);
  const refreshNow = useCallback((): void => refreshNowRef.current(), []);
  const [state, setState] = useState<ParticipationViewState>({
    data: null,
    loading: false,
    error: null,
  });

  useEffect(() => {
    if (!enabled) {
      refreshNowRef.current = () => undefined;
      setState({
        data: dataCacheRef.current[apiId] ?? null,
        loading: false,
        error: null,
      });
      return;
    }

    let active = true;
    let requestSequence = 0;
    let currentController: AbortController | null = null;
    let requestInFlight = false;
    let intervalId: number | null = null;

    setState((previous) => ({
      data: dataCacheRef.current[apiId]
        ?? (previous.data?.apiId === apiId ? previous.data : null),
      loading: true,
      error: null,
    }));

    const schedulePolling = (): void => {
      if (!active || intervalId !== null) return;
      intervalId = window.setInterval(() => {
        if (document.visibilityState !== "hidden") void refresh();
      }, PARTICIPATION_POLL_MS);
    };

    async function refresh(restartPolling = false): Promise<void> {
      if (!active || requestInFlight) return;

      if (restartPolling && intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }

      requestInFlight = true;
      const sequence = ++requestSequence;
      const controller = new AbortController();
      currentController = controller;
      let timedOut = false;
      const timeoutId = window.setTimeout(() => {
        timedOut = true;
        controller.abort("participation request timeout");
      }, PARTICIPATION_REQUEST_TIMEOUT_MS);

      setState((previous) => ({ ...previous, loading: true }));

      try {
        const response = await fetch(
          `/api/participation?apiId=${encodeURIComponent(apiId)}`,
          {
            method: "GET",
            cache: "no-store",
            headers: { Accept: "application/json" },
            signal: controller.signal,
          },
        );
        const payload: unknown = await response.json();
        if (response.status === 503 && isRecord(payload) && payload.ok === false
          && isRecord(payload.error) && payload.error.code === "PARTICIPATION_NOT_STARTED") {
          if (active && sequence === requestSequence) setState({ data: null, loading: false, error: null });
          return;
        }
        if (!response.ok) throw new Error("participation endpoint failed");
        const parsed = parseParticipationPayload(payload, apiId);

        if (!active || sequence !== requestSequence) return;
        dataCacheRef.current[apiId] = parsed.data;
        setState({ data: parsed.data, loading: false, error: null });
      } catch {
        if (
          !active
          || sequence !== requestSequence
          || (controller.signal.aborted && !timedOut)
        ) {
          return;
        }
        setState((previous) => ({
          ...previous,
          loading: false,
          error: "参加状況を取得できません。自動で再試行します。",
        }));
      } finally {
        window.clearTimeout(timeoutId);
        if (currentController === controller) currentController = null;
        requestInFlight = false;
        if (restartPolling) schedulePolling();
      }
    }

    const requestImmediateRefresh = (): void => void refresh(true);
    refreshNowRef.current = requestImmediateRefresh;

    void refresh();
    schedulePolling();
    const handleVisibility = (): void => {
      if (document.visibilityState === "visible") void refresh();
    };
    const handleResume = (): void => void refresh();
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("online", handleResume);
    window.addEventListener("focus", handleResume);
    window.addEventListener("pageshow", handleResume);

    return () => {
      active = false;
      requestSequence += 1;
      currentController?.abort();
      currentController = null;
      requestInFlight = false;
      if (refreshNowRef.current === requestImmediateRefresh) {
        refreshNowRef.current = () => undefined;
      }
      if (intervalId !== null) window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("online", handleResume);
      window.removeEventListener("focus", handleResume);
      window.removeEventListener("pageshow", handleResume);
    };
  }, [apiId, enabled]);

  if (state.data === null) {
    const cachedData = dataCacheRef.current[apiId] ?? null;
    if (cachedData === null) return { ...state, refreshNow };
    return {
      data: cachedData,
      loading: false,
      error: null,
      refreshNow,
    };
  }

  if (state.data.apiId !== apiId) {
    const cachedData = dataCacheRef.current[apiId] ?? null;
    return {
      data: cachedData,
      loading: enabled && cachedData === null,
      error: null,
      refreshNow,
    };
  }

  return { ...state, refreshNow };
}
